import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ApiResponse } from '@/types'

// EP-144 T144-008: the owner-facing platform-subscription reads.
//
// Wire shapes are typed against the committed API (SubscriptionBlueprint,
// SubscriptionInvoiceBlueprint) rather than the plan's prose: `pricing` and
// `next_invoice_preview` live INSIDE `data`, and every value the database
// stores as a numeric/decimal column arrives as a JSON STRING (ActiveSupport
// renders BigDecimal via to_s), while the engine's own figures arrive as
// numbers because SubscriptionEngine ends on `.round(2).to_f`.

export type SubscriptionTier = 'standard' | 'invitation'
export type SubscriptionStatus = 'active' | 'canceled'
export type SubscriptionBillingCycle = 'monthly'
export type SubscriptionInvoiceStatus = 'pending' | 'paid' | 'void'

/**
 * One engine line. `description_key` + `description_params` instead of a
 * rendered string (C-15) so mail and web each localize it themselves.
 * `quantity`, `unit_price` and `amount` are displayed exactly as returned.
 * The frontend never multiplies them.
 */
export interface SubscriptionItem {
  item_type: string
  description_key: string
  description_params: Record<string, string | number>
  quantity: number
  unit_price: number
  amount: number
}

export interface SubscriptionInvoicePreview {
  period_from: string
  period_to: string
  items: SubscriptionItem[]
  subtotal: number
  total: number
}

export interface SubscriptionPricingBracket {
  min: number
  /** null on the open-ended top bracket. */
  max: number | null
  monthly: number
  annual: number
}

export interface SubscriptionPricingTier {
  brackets: SubscriptionPricingBracket[]
}

/** Keyed by tier name, one entry per SubscriptionEngine::TIERS key. */
export type SubscriptionPricing = Record<string, SubscriptionPricingTier>

export interface Subscription {
  tier: SubscriptionTier
  billing_cycle: SubscriptionBillingCycle
  status: SubscriptionStatus
  started_at: string
  room_count: number
  next_invoice_preview: SubscriptionInvoicePreview
  pricing: SubscriptionPricing
}

/**
 * The index row (SubscriptionInvoiceBlueprint :summary).
 *
 * EP-145 C10b: the invoice carries no billing_cycle. It was a denormalised
 * copy of the subscription's, only ever held "monthly" and no view rendered
 * it. When annual billing ships it comes back as a frozen snapshot written at
 * invoice time, never as a live read off the subscription, the same rule
 * EP-136 applied to booking terms on a lease.
 */
export interface SubscriptionInvoice {
  id: number
  number: string
  tier: SubscriptionTier
  room_count: number
  period_from: string
  period_to: string
  /** Decimal column: arrives as a string such as "1024.0". */
  subtotal: string
  total: string
  status: SubscriptionInvoiceStatus
  due_at: string
  emailed_at: string | null
}

/**
 * The show payload (:detail view). `payment` is an object or null, never a
 * list, because there is exactly one payment per invoice (Q-8); `recorded_by`
 * is deliberately absent from the tenant payload (C-19).
 */
export interface SubscriptionInvoiceDetail extends SubscriptionInvoice {
  items: SubscriptionItem[]
  payment: { amount: string; paid_at: string | null; method: string | null } | null
}

export const subscriptionKeys = {
  all: ['subscription'] as const,
  detail: () => [...subscriptionKeys.all, 'detail'] as const,
  invoices: () => [...subscriptionKeys.all, 'invoices'] as const,
  invoiceList: (page: number) => [...subscriptionKeys.invoices(), 'list', page] as const,
  invoiceDetail: (id: number) => [...subscriptionKeys.invoices(), 'detail', id] as const,
}

/**
 * GET /api/subscription. Owner-only server-side (Q-9), so `enabled` lets the
 * caller hold the request back until the owner has actually opened the tab
 * rather than 403-ing on every Settings visit.
 */
export function useSubscription(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.detail(),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Subscription>>('/api/subscription')
      return data.data
    },
    enabled,
  })
}

/** GET /api/subscription_invoices. Returns the whole envelope: the list needs `meta` to paginate. */
export function useSubscriptionInvoices(page = 1, enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.invoiceList(page),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<SubscriptionInvoice[]>>(
        '/api/subscription_invoices',
        { params: { page } },
      )
      return data
    },
    enabled,
  })
}

/** GET /api/subscription_invoices/:id, the :detail view with items and the payment block. */
export function useSubscriptionInvoice(id: number | null | undefined) {
  return useQuery({
    queryKey: subscriptionKeys.invoiceDetail(id ?? 0),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<SubscriptionInvoiceDetail>>(
        `/api/subscription_invoices/${id}`,
      )
      return data.data
    },
    enabled: !!id,
  })
}
